import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AngularFireStorage } from '@angular/fire/compat/storage';
import { Observable, firstValueFrom, of } from 'rxjs';
import { catchError, first, map, switchMap, timeout } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { VehicleUtilities } from '../class/vehicle-utilities';
import { Breakdowns } from '../interface/breakdowns';
import { RO_STATES } from '../shared/lists/ro-states';
import { DataService } from './data.service';
import { ApiPaginatedOSQueryParams, ApiPaginatedResponse, PaginationService } from './pagination.service';
import { PaginatedOS } from '../interface/paginated-os';
import { RoListStateGroup } from '../shared/pipes/ro-state-group.pipe';
import { RoTypeService } from './ro-type.service';
import { CompanyService } from './company.service';
import { RoType } from '../interface/ro-type';
import { Operation } from '../interface/operation';
import { OperationService } from './operation.service';
import { PartState, partStates } from '../shared/lists/part-states';
import { LaborState, laborStates } from '../shared/lists/labor-states';
import { ApiOS, MkgOS, MkgOSFromFormDraft, OsLabor } from '../class/mkg-os';
import { SessionOSservice } from './session/session-os.service';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '../component/dialog/confirmation-dialog/confirmation-dialog.component';
import { LayoutService } from './layout.service';
import { SessionError } from './session/mkg-session';
import { UserService } from './user.service';
import moment from 'moment';
import { Utilities } from '../class/utilities';
import { roles } from '../shared/lists/roles';
import { IIndicatorsStatistics } from '../component/page/company/dashboard/info/totals.model';
import { User } from '../interface/user';
import { Company } from '@app/interface/company';
import { RoLogsService } from './ro-logs.service';
import { ILogOSCreateParams, RO_LOG_ACTION_TYPES } from '@app/interface/os-logs';
import { authorizationTypes } from '@app/shared/lists/authorization-types';
import { PRIORITY } from '@app/component/page/company/karban/components/card-dialog/card-dialog.component';
import { MkgOSPart } from '@app/class/mkg-os-part';
import { RoPartService } from './ro-part.service';
import { ObjectId } from '@app/shared/type-aliases/object-id';
import { RoLaborService } from './ro-labor.service';
import { FbImageDownloaderService, FIREBASE_STORAGE_PATHS } from './fb-image-downloader.service';

export type MatrixConfig = {
  companyParts: Company;
  companyLabors: Company;
  matrixIsFor: "parts" | "labors" | "none";
  /** Controls whether titles should be separated by CNPJ. If false, creates unified titles regardless of matrixIsFor */
  separateFinancialForTitles: boolean;
  roType: RoType;
  operationParts: Operation;
  operationLabors: Operation;
}

type RoUpdateDraftPayload = Partial<MkgOSFromFormDraft>;

type RoUpdateExtraPayload = Partial<Pick<MkgOS,
  | 'additionalState'
  | 'authDisposal'
  | 'budgeter'
  | 'chat'
  | 'checklistModel'
  | 'checklistModelItem'
  | 'complementaryApprove'
  | 'consultant'
  | 'deliveryForecast'
  | 'idFarmer'
  | 'importsBudget'
  | 'intermediary'
  | 'menu'
  | 'nfseCustomDestination'
  | 'nfseIndDest'
  | 'obsClient'
  | 'obsRejeicao'
  | 'observation'
  | 'observationNFSE'
  | 'osBlock'
  | 'packet'
  | 'presence'
  | 'priority'
  | 'recall'
  | 'rejectType'
  | 'review'
  | 'showPDF'
  | 'surveyModelItem'
  | 'tags'
>>;

export type RoUpdatePayload = RoUpdateDraftPayload & RoUpdateExtraPayload;
export type RoUpdateField = keyof RoUpdatePayload;
export type RoUpdateArg<K extends RoUpdateField = RoUpdateField> = {
  field: K;
  newValue: RoUpdatePayload[K];
};


@Injectable({
  providedIn: 'root'
})
export class RoService {
  private _isFromMatrix = false;
  public timestamps = new Map<string, Map<number, number>>();
  private roList = new Map<string, Map<string, MkgOS>>();
  private _http = inject(HttpClient);
  private _dataService = inject(DataService);
  private _dialog = inject(MatDialog);
  private _layout = inject(LayoutService);
  private _osSessionService = inject(SessionOSservice);
  private _storage = inject(AngularFireStorage);
  private _roTypeService = inject(RoTypeService);
  private _companyService = inject(CompanyService);
  private _operationService = inject(OperationService);
  private _userService = inject(UserService);
  private _roLogService = inject(RoLogsService);
  private _fbImageDownloaderService = inject(FbImageDownloaderService);
  public rosWithSessionError = new Set<string>();

  /**
   * Now, when getting an OS, the API will create a session and no one else will be able to obtain that OS, except by
   * providing the session hash.
   *
   * If the OS is in use by another user, the response will be different,
   * returning data with the session of the user who is using the OS and the session start date.
   *
   * Ask the user if they want to terminate the session of the user who is using the OS.
   *
   */
  async get(id: string, cnpj?: string, skipSessionValidation?: boolean, blockCloseSession?: boolean): Promise<MkgOS> {
    const url = `${environment.mkgoURL}/api/v1/os/${id}`;
    const options = await firstValueFrom(this._dataService.httpOptions(cnpj ?? this._isFromMatrix));
    const params = (options.params || new HttpParams()).append("hash", this._osSessionService.sessionHash);
    options.params = params;
    return firstValueFrom(this._http.get<ApiOS>(url, options).pipe(
      first(),
      catchError(async error => { // if the OS is in use, the API will return an error
        if (error.status === 400 && (error.error as SessionError).userId) {
          const sessionError = error.error as SessionError;
          const sessionTimeStarted = new Date(+(sessionError as any).hash)
          const timeFormated = Utilities.transformTime(moment().diff(sessionTimeStarted, "seconds"));

          const currentUser = this._dataService.user;// user que tenta entrar
          let user: User; // ultimo acesso a OS
          try {
            user = await this._userService.get(sessionError.userId);
          } catch (err) {
            if (err.status === 400) { // user não pertence mais a empresa
              return await firstValueFrom(this._osSessionService.closeSession(id, sessionError._id));
            }
            console.error("Usuário não encontrado.", err)
          }

          if (skipSessionValidation) {
            return await firstValueFrom(this._osSessionService.closeSession(id, sessionError._id));
          };

          this.rosWithSessionError.add(id);

          // if (user?.id === currentUser.id) {
          //   return await firstValueFrom(this._osSessionService.closeSession(id, sessionError._id));
          // } attempt to solve the error of generate two invoices

          
          const isAdmin = currentUser.getRoleIn(this._dataService.company) === roles.admin.id;
          const sameUserAsThelogged = user?.id === currentUser.id;

          let text: string;
          if (blockCloseSession) {
            text = "OS_IN_USE_CANNOT_CLOSE";
          } else {
            text = isAdmin || sameUserAsThelogged ? "OS_IN_USE" : "OS_IN_USE_NON_ADMIN";
          }

          const result = await firstValueFrom(this._dialog.open(ConfirmationDialogComponent, {
            data: {
              text,
              param: {
                ...sessionError,
                name: user.name,
                email: user.email,
                date: timeFormated
              },
              uniqueAction: (!isAdmin && !sameUserAsThelogged) || (blockCloseSession),
            }
          }).afterClosed());
          if (((result && isAdmin) || (sameUserAsThelogged && result)) && !blockCloseSession) {
            const os = await firstValueFrom(this._osSessionService.closeSession(id, sessionError._id));
            return os;
          }

          if (!result || blockCloseSession) {
            throw { ...error, roInUse: true };
          }
        }
        throw error;
      }),
      map(resp => {
        return new MkgOS({ ...resp, cnpj });
      }),
    ));
  }

  /**
   * @deprecated
   * @param roState The max state to get, for example:
   * if roState is 9, the API will return ROs with currentState between 1 and 9.
   * @param maxTimestamp The last timestamp know
   */
  async getAll(roState: number, maxTimestamp = 0, params?: {
    startDate: string,
    endDate: string
  }, cnpj?: string): Promise<Map<string, MkgOS>> {
    if (roState == RO_STATES.all) {
      roState = RO_STATES.evaluation;
    } else if (roState == RO_STATES.rejected) {
      roState = RO_STATES.finished;
    }
    const timestamps = this.timestamps.get(this._dataService.company.id)
      || new Map<number, number>();
    this.timestamps.set(this._dataService.company.id, timestamps);

    timestamps.forEach((timestamp, state) => {
      if (state >= roState) {
        maxTimestamp = Math.max(maxTimestamp, timestamp);
      }
    });
    const url = `${environment.mkgoURL}/api/v1/os`;
    const options = {
      headers: { 'Company-CNPJ': cnpj || this._dataService.company.cnpj },
      params: { state: (roState + 1).toString(), timestamp: maxTimestamp.toString() }
    };

    if (params) {
      options.params = {
        // state: options.params.state,
        startDate: params.startDate,
        endDate: params.endDate
      } as any
    }

    const osArray = await firstValueFrom(this._http.get<{ os: ApiOS[] }>(url, options)
      .pipe(
        first(),
        map(resp => resp.os.reverse()),
        map(list => list.map(apiOS => {
          try {
            const os = new MkgOS(apiOS);
            return os
          } catch (error) {
            console.warn("Não foi possível converter o seguint objeto os em MKGOS", apiOS);
            throw error;
          }
        }))
      ));

    const osMap = new Map<string, MkgOS>();
    for (const os of osArray) {
      osMap.set(os.id, os)
    }

    return osMap;
  }

  getPaginated(statesParams: RoListStateGroup | RoListStateGroup[], queryParams: ApiPaginatedOSQueryParams = { all: 1 }, cnpj?: string): Observable<ApiPaginatedResponse<PaginatedOS>> {
    const url = `${environment.mkgoURL}/api/v1/os/mobile`;
    let params;
    // if (queryParams.search) {
    // params = queryParams;
    // } else {
    const states = this._getStateCodes(statesParams);
    params = PaginationService.getParams({ ...queryParams, state: states });
    // }
    const _cnpj = cnpj !== undefined ? cnpj : this._isFromMatrix;

    if (queryParams.user) {
      queryParams.user = this._transformSpecialCharacters(queryParams.user);
    }

    return this._dataService.httpOptions(_cnpj, params).pipe(
      switchMap(options => this._http.get<ApiPaginatedResponse<PaginatedOS>>(url, options)),
      first(),
      switchMap(resp => {
        if (queryParams.all && resp.hasNextPage) {
          queryParams.limit = resp.totalDocs;
          params = PaginationService.getParams(queryParams);
          return this._dataService.httpOptions(cnpj, params).pipe(
            switchMap(options2 => this._http.get<ApiPaginatedResponse<PaginatedOS>>(url, options2)),
            first()
          );
        } else if (Array.isArray(resp)) {
          return of({
            docs: resp,
            hasNextPage: false,
            hasPrevPage: false,
            limit: queryParams.limit,
            nextPage: false,
            page: queryParams.page,
            prevPage: false,
            totalDocs: resp.length,
            pagingCounter: undefined,
            totalPages: 0
          } as ApiPaginatedResponse<PaginatedOS>);
        } else {
          return of(resp);
        }
      }),
      map(resp => { // divide some values by 100
        resp.docs.map(os => {
          os.cnpj = cnpj || this._dataService.company.cnpj;

          // if (state === 'rejected') {
          if (!Array.isArray(statesParams) && statesParams === 'rejected'){
            os.currentState = RO_STATES.rejected;
          }
          os.liquidValue = (os.liquidValue || 0) / 100;

          os.parts.map(ospart => {
            ospart.purchaseValue = (ospart.purchaseValue || 0) / 100;
            ospart.purchaseAverage = (ospart.purchaseAverage || 0) / 100;
            ospart.saleValue = (ospart.saleValue || 0) / 100;
            ospart.liquidValue = (ospart.liquidValue || 0) / 100;
            return ospart;
          });

          os.labors.map(oslabor => {
            oslabor.saleValue = (oslabor.saleValue || 0) / 100;
            oslabor.liquidValue = (oslabor.liquidValue || 0) / 100;
            oslabor.discountValue = (oslabor.discountValue || 0) / 100;
            oslabor.discountProp = (oslabor.discountProp || 0) / 100;
            oslabor.discountPropGeral = (oslabor.discountPropGeral || 0) / 100;
            return oslabor;
          });

          os.thirdPartyServices.map(outsourced => {
            outsourced.collectedValue = (outsourced.collectedValue || 0) / 100;
            outsourced.paymentValue = (outsourced.paymentValue || 0) / 100;
            return outsourced;
          });

          os.liquidValueParts = os.parts.reduce((p1, p2) => p1 + p2.liquidValue, 0);
          os.liquidValueLabors = os.labors.reduce((l1, l2) => l1 + l2.liquidValue, 0);
          os.outsourcedPaid = os.thirdPartyServices.reduce((s1, s2) => s1 + s2.paymentValue, 0);
          os.outsourcedCollected = os.thirdPartyServices.reduce((s1, s2) => s1 + s2.collectedValue, 0);
          os.partsGrossValue = os.parts.filter(osPart => osPart.available !== partStates.rejected.id).reduce((sum, roPart) => sum + roPart.amount * roPart.saleValue, 0);
          os.laborsGrossValue = os.labors.filter(osLabor => osLabor.available !== laborStates.rejected.id).reduce((sum, osLabor) => sum + osLabor.amount * osLabor.saleValue, 0);
          os.grossValue = os.partsGrossValue + os.laborsGrossValue + os.outsourcedCollected;

          return os;
        });

        return resp;
      })
    );
  }

  private _transformSpecialCharacters(email: string) {
    return email.replaceAll(/@/g, "%40");
  }

  async getOsByPlate(plate: string, filters?: any): Promise<MkgOS[]> {
    const unmasked = VehicleUtilities.removeDiacritics(plate);

    const url = `${environment.mkgoURL}/api/v1/os/vehicle/${unmasked}`;
    const options = await firstValueFrom(this._dataService.httpOptions(this._isFromMatrix));

    let params = options.params || new HttpParams();

    if (filters) {
      if (filters.states) params = params.set('states', filters.states.join(','));
      if (filters.user) params = params.set('user', filters.user);
      if (filters.openedStart) params = params.set('openedStart', filters.openedStart);
      if (filters.openedEnd) params = params.set('openedEnd', filters.openedEnd);
      if (filters.closedStart) params = params.set('closedStart', filters.closedStart);
      if (filters.closedEnd) params = params.set('closedEnd', filters.closedEnd);
      if (filters.part) params = params.set('part', filters.part);
      if (filters.labor) params = params.set('labor', filters.labor);
      if (filters.mechanic) params = params.set('mechanic', filters.mechanic);
    }

    options.params = params;

    return firstValueFrom(this._http.get<{
      os: any[],
      vehicle: { id: string }
    }>(url, options).pipe(
      first(),
      map(resp => resp.os),
      map(osList => osList.map(apiOs => new MkgOS(apiOs)))
    ));
  }

  async getOsByClient(client: string, filters?: any): Promise<MkgOS[]> {
    const url = `${environment.mkgoURL}/api/v1/os/client/${client}`;
    const options = await firstValueFrom(this._dataService.httpOptions(this._isFromMatrix));

    let params = options.params || new HttpParams();

    if (filters) {
      if (filters.states) params = params.set('states', filters.states.join(','));
      if (filters.user) params = params.set('user', filters.user);
      if (filters.openedStart) params = params.set('openedStart', filters.openedStart);
      if (filters.openedEnd) params = params.set('openedEnd', filters.openedEnd);
      if (filters.closedStart) params = params.set('closedStart', filters.closedStart);
      if (filters.closedEnd) params = params.set('closedEnd', filters.closedEnd);
      if (filters.part) params = params.set('part', filters.part);
      if (filters.labor) params = params.set('labor', filters.labor);
      if (filters.mechanic) params = params.set('mechanic', filters.mechanic);
    }

    options.params = params;

    const response = await firstValueFrom(this._http.get<{ clientOses: any[] }>(url, options)
      .pipe(
        first(),
        map(resp => resp.clientOses),
        map(rolist => rolist.map(ro => new MkgOS({ ...ro, id: ro._id })))
      ));
    return response;
  }

  async getPdf(id: string): Promise<string> {
    const url = `${environment.mkgoURL}/api/v1/pdf/os/${id}`;
    const header = await firstValueFrom(this._dataService.httpOptions(this._isFromMatrix));
    const response: any = firstValueFrom(await this._http.get(url, header));
    return response.url;
  }

  async downloadPdf(key: string) {
    const url = `${environment.mkgoURL}/short-pdf/${key}`;
    const options = {
      headers: { 'Company-CNPJ': this._dataService.company.cnpj }, responseType: 'blob' as 'blob'
    };
    return firstValueFrom(this._http.get(url, options));
  }

  async add(formValue: any, optionData: { reason: string }) {
    const os = MkgOS.fromForm(formValue);
    const { reason } = optionData
    os['lastUpdate'] = new Date().toISOString();
    const url = `${environment.mkgoURL}/api/v1/os`;
    const header = await firstValueFrom(this._dataService.httpOptions(this._isFromMatrix));

    try {
      let response = await firstValueFrom(this._http.post<{
        id: string,
        code: string
      }>(url, os, header));
      
      await this._roLogService.create({
        actionType: RO_LOG_ACTION_TYPES.CREATE,
        osId: response.id,
        statusInTheLogRecord: os.currentState ?? RO_STATES.budget,
        reason,
        updatedFields: this._getFieldsFromOsCreation(formValue),
      })

      return response;
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  private _getFieldsFromOsCreation(form: Object): ILogOSCreateParams["updatedFields"] {
    const keys = Object.keys(form);
    const changedFields: ILogOSCreateParams["updatedFields"] = [];

    for (const key of keys) {
      if (key === "id") continue;

      const value = form[key];

      if (!value) continue;
      
      changedFields.push({
        oldValue: null,
        fieldName: key as any,
        newValue: value
      })
    }

    return changedFields;
  }

  /** state 2(budget) to state 4(authorization) */
  async requestAuthorization(ro: MkgOS, cnpj?: string) {
    const url = `${environment.mkgoURL}/api/v1/os/${ro.id}/authorization`;
    let body = {
      discountLabors: ro.discountLabors,
      discountParts: ro.discountParts,
      lastUpdate: new Date().toISOString()
    }
    if (ro.deliveryForecast) {
      body['deliveryForecast'] = new Date(ro.deliveryForecast).toISOString();
    }
    const header = await firstValueFrom(this._dataService.httpOptions(cnpj ?? this._isFromMatrix));
    try {
      await firstValueFrom(this._http.post(url, body, header));
      
      await this._roLogService.create({
        actionType: RO_LOG_ACTION_TYPES.REQUEST_AUTHORIZATION,
        osId: ro.id,
        statusInTheLogRecord: ro.currentState,
        updatedFields: [
          {
            fieldName: "currentState",
            newValue: RO_STATES.authorization,
            oldValue: ro.currentState
          }
        ]
      })
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  /** return budget to state 2 */
  async returnToBudget(OS: MkgOS) {
    const url = `${environment.mkgoURL}/api/v1/os/${OS.id}/budget`;
    const body = { lastUpdate: new Date().toISOString() };
    const header = await firstValueFrom(this._dataService.httpOptions(this._isFromMatrix));
    try {
      await firstValueFrom(this._http.post(url, body, header));
      
      await this._roLogService.create({
        actionType: RO_LOG_ACTION_TYPES.RETURN_TO_BUDGET,
        osId: OS.id,
        statusInTheLogRecord: OS.currentState,
        updatedFields: [
          {
            fieldName: "currentState",
            newValue: RO_STATES.budget,
            oldValue: OS.currentState
          }
        ]
      })
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  /** from state 4(authorization) to state 5(appointment) */
  async authorize(OS: MkgOS, authorizationType: number, optionData: {
    cnpj?: string;
    reason?: string;
  }) {
    const url = `${environment.mkgoURL}/api/v1/os/${OS.id}/authorization/${authorizationType}`;
    const body = { 'lastUpdate': new Date().toISOString() }
    const { cnpj, reason } = optionData;
    const header = await firstValueFrom(this._dataService.httpOptions(cnpj ?? this._isFromMatrix));
    try {
      await firstValueFrom(this._http.post(url, body, header));
      
      await this._roLogService.create({
        actionType: RO_LOG_ACTION_TYPES.AUTHORIZE,
        osId: OS.id,
        statusInTheLogRecord: OS.currentState,
        updatedFields: [
          {
            fieldName: "currentState",
            newValue: authorizationType === authorizationTypes.rejected.id ? RO_STATES.rejected : RO_STATES.appointment,
            oldValue: OS.currentState
          }
        ],
        reason
      })
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  /** Make the appointment, no changing the ro state **/
  async makeAppointment(id: string, appointment: Date, cnpj?: string) {
    const url = `${environment.mkgoURL}/api/v1/os/${id}/appointment`;
    const body = {
      'appointment': appointment.toISOString(),
      'lastUpdate': new Date().toISOString()
    }
    const header = await firstValueFrom(this._dataService.httpOptions(cnpj ?? this._isFromMatrix));
    return firstValueFrom(this._http.post(url, body, header));
  }

  /** from state 5(appointment) or 6(ready to start) to state 7(started) */
  async open(ro: MkgOS, optionData: {
    cnpj?: string;
    reason?: string;
  }) {
    const url = `${environment.mkgoURL}/api/v1/os/${ro.id}/start`;
    const body = { 'lastUpdate': new Date().toISOString() };
    const { cnpj, reason } = optionData;
    
    const header = await firstValueFrom(this._dataService.httpOptions(cnpj ?? this._isFromMatrix));
    try {
      await firstValueFrom(this._http.post(url, body, header));
      
      await this._roLogService.create({
        actionType: RO_LOG_ACTION_TYPES.OPEN,
        osId: ro.id,
        statusInTheLogRecord: ro.currentState,
        updatedFields: [
          {
            fieldName: "currentState",
            newValue: RO_STATES.started,
            oldValue: ro.currentState
          }
        ],
        reason
      })

      return;
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  /** from state 7(started) to state 8(to receive) */
  async close(OS: MkgOS) {
    const url = `${environment.mkgoURL}/api/v1/os/${OS.id}/finish`;
    const body = { 'lastUpdate': new Date().toISOString() };
    const header = await firstValueFrom(this._dataService.httpOptions(this._isFromMatrix));

    try {
      await firstValueFrom(this._http.post(url, body, header));
      
      await this._roLogService.create({
        actionType: RO_LOG_ACTION_TYPES.CLOSE,
        osId: OS.id,
        statusInTheLogRecord: OS.currentState,
        updatedFields: [
          {
            fieldName: "currentState",
            newValue: RO_STATES.finished,
            oldValue: OS.currentState
          }
        ]
      })

      return;
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  /** form state 8(to receive) to state 10(finished) */
  async finish(OS: MkgOS, extraOptions: {
    cnpj?: string;
    reason: string;
    receiveDate?: Date | string;
    invoices: { invoiceId: string; invoiceNumber: string; }[];
  }) {
    if (!OS.id) {
      throw new Error("id is required");
    }
    const { reason, cnpj, receiveDate, invoices } = extraOptions;
    const url = `${environment.mkgoURL}/api/v1/os/${OS.id}/end`;
    
    const body: { lastUpdate: string; receiveDate?: string } = {
      'lastUpdate': new Date().toISOString()
    };
    
    if (receiveDate) {
      body.receiveDate = typeof receiveDate === 'string' ? receiveDate : new Date(receiveDate).toISOString();
    }
    
    const header = await firstValueFrom(this._dataService.httpOptions(cnpj ?? this._isFromMatrix));
    try {
      await firstValueFrom(this._http.post(url, body, header));
      
      await this._roLogService.create({
        actionType: RO_LOG_ACTION_TYPES.ISSUE_INVOICES,
        osId: OS.id,
        statusInTheLogRecord: OS.currentState,
        invoicesRelated: invoices,
        updatedFields: [
          {
            fieldName: "currentState",
            newValue: RO_STATES.finished,
            oldValue: OS.currentState
          }
        ],
        reason
      })

      return;
    } catch (error) {
      console.error(error);
      throw error;
    }
  }


  /** Update fields of OS
   *
   * if you want to change `paymentCondition`, use a [PaymentService](./payment.service.ts) instance
   *
   * if you want to change the `checklist` array, use a [ChecklistService](./checklist.service.ts) instance
  *
   * if you want to change `breakdowns`, use the function {@link saveBreakdows}
   */
  async update(id: string, args: RoUpdateArg[], ro: MkgOS, skipUpdatedFields: boolean = false, extraInfo?: string) {
    const url = `${environment.mkgoURL}/api/v1/os/${id}`;
    const params: Partial<RoUpdatePayload> = { };
    const updatedFields: ILogOSCreateParams["updatedFields"] = [];
    let actionType = RO_LOG_ACTION_TYPES.UPDATE_RO;

    if (!args.length) {
      return;
    }

    for (const arg of args) {
      switch (arg.field) {
        case "menu": 
          actionType = RO_LOG_ACTION_TYPES.ADDED_CARTE
          break;
        case "deliveryForecast": {
          const newDate = moment(arg.newValue as string | undefined);
          const oldDate = moment(ro.deliveryForecast);

          if (!newDate.isSame(oldDate)) {
            (params as Record<string, unknown>)[arg.field] = arg.newValue;

            if (!skipUpdatedFields) {
              updatedFields.push({
                fieldName: arg.field,
                newValue: arg.newValue,
                oldValue: ro.deliveryForecast
              });
            }
          }
          break;
        }
        case "tags": {
          const newTags = arg.newValue as MkgOS['tags'];
          let allEqual = true;

          if (ro[arg.field]?.length && (ro[arg.field]?.length === newTags?.length)) {
            const arrayWithoutChanges = ro[arg.field];

            newTags?.forEach((tag: { description: string }, index: number) => {
              if (!allEqual) {
                return;
              }
              
              const lastValue = arrayWithoutChanges[index];

              if (!lastValue) {
                allEqual = false;
              }

              if ((lastValue.description !== tag.description) && allEqual) {
                allEqual = false;
              }
            });
          } else {
            allEqual = false;
          }
          
          if (!allEqual && (newTags?.length || ro[arg.field]?.length)) {
            (params as Record<string, unknown>)[arg.field] = newTags;
            
            if (!skipUpdatedFields) {
              updatedFields.push({
                fieldName: arg.field,
                newValue: newTags,
                oldValue: ro[arg.field]
              });
            }
          }
          break;
        }
        case "priority":
          if (arg.newValue != PRIORITY[ro[arg.field]]) {
            (params as Record<string, unknown>)[arg.field] = arg.newValue;

            if (!skipUpdatedFields) {
              updatedFields.push({
                fieldName: arg.field,
                newValue: arg.newValue,
                oldValue: PRIORITY[ro[arg.field]]
              });
            }
          }
          break;
        case "vehicle":
        case "type":
        case "operation":
        case "client":
          const id = typeof ro[arg.field] === "string" ? ro[arg.field] : (ro[arg.field]?.id ?? ro[arg.field]);

          if (id != arg.newValue) {
            (params as Record<string, unknown>)[arg.field] = arg.newValue;

            if (!skipUpdatedFields) {
              updatedFields.push({
                fieldName: arg.field,
                newValue: arg.newValue,
                oldValue: id
              });
            }
          }
          break;
        default:
          if (arg.newValue != ro[arg.field]) {
            (params as Record<string, unknown>)[arg.field] = arg.newValue;

            if (!skipUpdatedFields) {
              updatedFields.push({
                fieldName: arg.field,
                newValue: arg.newValue,
                oldValue: ro[arg.field]
              });
            }
          }
      }
    }

    const paramsKeys = Object.keys(params);

    if (!paramsKeys.length) {
      return;
    }
  
    try {
      const header = await firstValueFrom(this._dataService.httpOptions(this._isFromMatrix));
      await firstValueFrom(this._http.put(url, JSON.stringify({
        ...params, 
        lastUpdate: new Date().toISOString()
      }), header));
      
      await this._roLogService.create({
        actionType,
        osId: ro.id,
        statusInTheLogRecord: ro.currentState,
        updatedFields,
        reason: extraInfo,
      });

      return;
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  /**
   * Atualiza descontos da OS no backend
   * Backend recalcula TODOS os valores e redistribui entre itens
   * 
   * @param ro OS com descontos a aplicar
   * @param changedField Campo que foi alterado pelo usuário: 'percentage', 'absolute', 'netValue' ou null para enviar todos
   * @returns Promise<MkgOS> OS completa recalculada pelo backend
   * 
   * @remarks
   * - Quando `changedField` é especificado, envia APENAS o campo alterado (percentual OU valor absoluto)
   * - Backend calcula e retorna todos os valores derivados (liquidValue, discountProp, etc)
   * - Response já vem em REAIS (não dividir por 100)
   * - Backend redistribui descontos proporcionalmente entre itens
   * - Se `changedField` é null, envia todos os valores (comportamento legacy)
   * 
   * @example
   * ```typescript
   * // Usuário alterou o percentual de peças
   * const ro = await roService.get('123');
   * ro.discountParts = 10;
   * const updated = await roService.updateDiscount(ro, 'percentage');
   * console.log(updated.liquidValueParts); // Calculado pelo backend
   * console.log(updated.parts[0].discountProp); // Redistribuído pelo backend
   * ```
   */
  async updateDiscount(ro: MkgOS, changedField?: 'percentage' | 'absolute' | 'netValue' | null): Promise<MkgOS> {
    // ✅ VALIDAÇÃO 1: OS deve ter ID
    if (!ro?.id) {
      const error = '[RoService] updateDiscount: OS sem ID';
      console.error(error, { ro });
      throw new Error(error);
    }
    
    // ✅ VALIDAÇÃO 2: Percentuais entre 0 e 100
    if (changedField === 'percentage') {
      if (ro.generalDiscount < 0 || ro.generalDiscount > 100) {
        const error = `[RoService] updateDiscount: generalDiscount inválido (${ro.generalDiscount}). Deve estar entre 0 e 100.`;
        console.error(error);
        throw new Error(error);
      }
      if (ro.discountParts < 0 || ro.discountParts > 100) {
        const error = `[RoService] updateDiscount: discountParts inválido (${ro.discountParts}). Deve estar entre 0 e 100.`;
        console.error(error);
        throw new Error(error);
      }
      if (ro.discountLabors < 0 || ro.discountLabors > 100) {
        const error = `[RoService] updateDiscount: discountLabors inválido (${ro.discountLabors}). Deve estar entre 0 e 100.`;
        console.error(error);
        throw new Error(error);
      }
    }
    
    // ✅ VALIDAÇÃO 3: Valores absolutos não negativos
    if (changedField === 'absolute') {
      if (ro.valueDiscountOs < 0) {
        const error = `[RoService] updateDiscount: valueDiscountOs não pode ser negativo (${ro.valueDiscountOs}).`;
        console.error(error);
        throw new Error(error);
      }
      if (ro.valueDiscountParts < 0) {
        const error = `[RoService] updateDiscount: valueDiscountParts não pode ser negativo (${ro.valueDiscountParts}).`;
        console.error(error);
        throw new Error(error);
      }
      if (ro.valueDiscountLabors < 0) {
        const error = `[RoService] updateDiscount: valueDiscountLabors não pode ser negativo (${ro.valueDiscountLabors}).`;
        console.error(error);
        throw new Error(error);
      }
    }
    
    // ✅ VALIDAÇÃO 4: Valor líquido não pode ser negativo
    if (changedField === 'netValue') {
      if (ro.liquidValueParts !== undefined && ro.liquidValueParts !== null && ro.liquidValueParts < 0) {
        const error = `[RoService] updateDiscount: liquidValueParts não pode ser negativo (${ro.liquidValueParts}).`;
        console.error(error);
        throw new Error(error);
      }
      if (ro.liquidValueLabors !== undefined && ro.liquidValueLabors !== null && ro.liquidValueLabors < 0) {
        const error = `[RoService] updateDiscount: liquidValueLabors não pode ser negativo (${ro.liquidValueLabors}).`;
        console.error(error);
        throw new Error(error);
      }
    }
    
    const url = `${environment.mkgoURL}/api/v1/os/${ro.id}/discount`;
    
    let params: any;
    
    if (changedField === 'percentage') {
      // Usuário alterou percentual - enviar APENAS percentuais
      params = {
        discountParts: ro.discountParts ?? 0,
        discountLabors: ro.discountLabors ?? 0,
        generalDiscount: ro.generalDiscount ?? 0,
        lastUpdate: new Date().toISOString()
      };
    } else if (changedField === 'absolute') {
      // Usuário alterou valor absoluto - enviar APENAS valores absolutos
      params = {
        valueDiscountParts: ro.valueDiscountParts ?? 0,
        valueDiscountLabors: ro.valueDiscountLabors ?? 0,
        valueDiscountOs: ro.valueDiscountOs ?? 0,
        lastUpdate: new Date().toISOString()
      };
    } else if (changedField === 'netValue') {
      // Usuário alterou valor líquido - enviar valores finais desejados
      params = {
        lastUpdate: new Date().toISOString()
      };

      if (ro.liquidValueParts !== undefined && ro.liquidValueParts !== null) {
        params.liquidValueParts = ro.liquidValueParts;
      }

      if (ro.liquidValueLabors !== undefined && ro.liquidValueLabors !== null) {
        params.liquidValueLabors = ro.liquidValueLabors;
      }
    } else {
      // Comportamento legacy - enviar todos os valores + retenções
      params = {
        valueDiscountParts: ro.valueDiscountParts ?? 0,
        valueDiscountLabors: ro.valueDiscountLabors ?? 0,
        valueDiscountOs: ro.valueDiscountOs ?? 0,
        retISS: ro.retISS ?? 0,
        valDeducoesParts: ro.valDeducoesParts ?? 0,
        valDeducoesLabors: ro.valDeducoesLabors ?? 0,
        lastUpdate: new Date().toISOString()
      };
    }
    
    const header = await firstValueFrom(this._dataService.httpOptions(this._isFromMatrix));
    const response = await firstValueFrom(
      this._http.post<ApiOS>(url, params, header)
    );
    
    // Backend retorna valores em REAIS (não dividir por 100)
    return new MkgOS(response, true);
  }

  async createBudgetCode(ro: MkgOS): Promise<number> {
    const url = `${environment.mkgoURL}/api/v1/os/${ro.id}`;
    let options = await firstValueFrom(this._dataService.httpOptions(this._isFromMatrix));
    const params = new HttpParams().append("budgetCode", "1");
    options['params'] = params;
    const resp = firstValueFrom(await this._http.post(url, {}, options));

    // update local roList
    if (resp['budgetCode']) {
      const roList = this.roList.get(this._dataService.company.id) || new Map<string, MkgOS>();
      ro.budgetCode = resp['budgetCode']
      roList.set(ro.id, ro)
      this.roList.set(this._dataService.company.id, roList);
      return resp['budgetCode']
    }
    return undefined
  }


  async createCodeSystem(ro: MkgOS): Promise<number> {
    const url = `${environment.mkgoURL}/api/v1/os/${ro.id}`;
    let options = await firstValueFrom(this._dataService.httpOptions(this._isFromMatrix));
    const params = new HttpParams().append("codeSystem", "1");
    options['params'] = params;
    const resp = firstValueFrom(await this._http.post(url, {}, options));

    // update local roList
    if (resp['codeSystem']) {
      const roList = this.roList.get(this._dataService.company.id) || new Map<string, MkgOS>();
      ro.codeSystem = resp['codeSystem']
      roList.set(ro.id, ro)
      this.roList.set(this._dataService.company.id, roList);
      return resp['codeSystem']
    }
    return undefined
  }


  /** @todo test */
  public async storeDamagesImage(osId: string, blob: Blob): Promise<string> {
    const path = FIREBASE_STORAGE_PATHS.breakdowns({ companyId: this._dataService.company.id, osId });
    // save the image into firebase
    await this._storage.upload(path, blob);

    // retrieve public url for image
    return firstValueFrom(this._storage.ref(path).getDownloadURL());
  }

  /** @todo test */
  public async storeAditionalDamagesImage(osId: string, blob: Blob, index: number): Promise<string> {
    const path = FIREBASE_STORAGE_PATHS.damagesAttachments({ companyId: this._dataService.company.id, osId, index });
    // save the image into firebase
    await this._storage.upload(path, blob);

    // retrieve public url for image
    return firstValueFrom(this._storage.ref(path).getDownloadURL());
  }

  public async storeSignatureImage(osId: string, blob: Blob): Promise<string> {
    // prepare path
    const path = FIREBASE_STORAGE_PATHS.signature({ companyId: this._dataService.company.id, osId });

    // save the image into firebase
    await this._storage.upload(path, blob);

    // retrieve public url for image
    return firstValueFrom(this._storage.ref(path).getDownloadURL());
  }

  async getBlob(firebaseUrl: string): Promise<Blob> {
    return firstValueFrom(this._fbImageDownloaderService.getBlob({ url: firebaseUrl }));
  }

  async saveBreakdows(osId: string, breakdowns: Breakdowns, osState: RO_STATES) {
    const url = `${environment.mkgoURL}/api/v1/os/${osId}/breakdowns`;
    const options = await firstValueFrom(this._dataService.httpOptions(this._isFromMatrix));

    try {
      const response = await firstValueFrom(this._http.post<{ id: string }>(url, breakdowns, options));
      
      await this._roLogService.create({
        actionType: RO_LOG_ACTION_TYPES.SAVE_BREAKDOWNS,
        osId: osId,
        statusInTheLogRecord: osState,
      })

      return response;
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  private _getStateCodes(stateGroup: RoListStateGroup | RoListStateGroup[]): number[] {
    if (Array.isArray(stateGroup)) {
      const allStates = stateGroup.flatMap(group => this._getStateCodes(group));
      return [...new Set(allStates)];
    }

    switch (stateGroup) {
      case 'budget':
        return [
          RO_STATES.budget,
          RO_STATES.authorization,
          RO_STATES.appointment,
          RO_STATES.readyToStart
        ];
      case 'opened':
        return [RO_STATES.started];
      case 'integrated_opened':
        return [
          RO_STATES.started,
          RO_STATES.toReceive,
          RO_STATES.evaluation
        ];
      case 'closed':
        return [RO_STATES.toReceive, RO_STATES.evaluation];
      case 'ended':
        return [RO_STATES.finished];
      case 'rejected':
        return [RO_STATES.rejected];
      case 'closed-budget':
        return [RO_STATES.closedBudget];
      case "all":
        return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]
      default:
        return []
    }
  }

  /** Find cnpj to use on invoices and titles */
  public async getMatrixConfigFor(OS: MkgOS, extraData?: { roTypeId: string, operationId: string }): Promise<MatrixConfig> {
    let companyParts = this._dataService.company;
    let companyLabors = this._dataService.company;
    let operationParts: Operation;
    let operationLabors: Operation;
    let matrixIsFor: "parts" | "labors" | "none" = "none";
    let roType: RoType;
    let roTypeId: string, operationId: string;

    if (extraData?.roTypeId && extraData?.operationId) {
      roTypeId = extraData.roTypeId;
      operationId = extraData.operationId;
    } else {
      roTypeId = typeof OS.type === "string" ? OS.type : OS.type?.id;
      operationId = OS?.operationId ?? (OS?.operation?.id ?? OS?.operation as any);
    }

    if (roTypeId) {
      roType = await this._roTypeService.get(roTypeId);

      if (roType) {
          if (roType.nfeCnpj?.trim() && roType.nfeCnpj != companyParts.cnpj.trim()) {
            companyParts = await this._companyService.getByCnpj(roType.nfeCnpj, true);
            matrixIsFor = 'parts';
          }
          if (roType.nfseCnpj?.trim() && roType.nfseCnpj != companyLabors.cnpj.trim()) {
            companyLabors = await this._companyService.getByCnpj(roType.nfseCnpj, true);
            matrixIsFor = 'labors';
          }
        if (roType.operation && matrixIsFor !== "none") {
          let operation: Operation;
          try {
            operation = await this._operationService.getById(roType.operation);
          } catch (error) {
            console.error("Operação do tipo de OS não encontrada", { id: roType.operation })
          }
          if (operation) {
            if (matrixIsFor === "parts") {
              operationParts = operation;
            } else if (matrixIsFor == "labors") {
              operationLabors = operation;
            }
          }
        }
      }
    }

    if (!operationParts && !operationLabors) {
      const commonOperation = await this._operationService.getById(operationId);
      operationLabors = commonOperation;
      operationParts = commonOperation;
    } else {
      if (!operationParts) {
        try {
          operationParts = await this._operationService.getById(operationId);
        } catch (error) {
          console.error("Operação da OS não encontrada", { id: operationId })
        }
      }
      if (!operationLabors) {
        try {
          operationLabors = await this._operationService.getById(operationId);
        } catch (error) {
          console.error("Operação da OS não encontrada", { id: operationId })
        }
      }
    }

    // Determine if financial should be separated for titles
    // If separateFinancial is explicitly false, titles are unified (matrixIsFor becomes "none")
    // But CNPJs remain separate for invoice generation
    const separateFinancialForTitles = roType?.separateFinancial !== false;

    return { 
      companyParts, 
      companyLabors, 
      matrixIsFor, 
      separateFinancialForTitles,
      roType, 
      operationParts, 
      operationLabors 
    }
  }


  async getStatistics(receiveDateStart: string, receiveDateEnd: string): Promise<IIndicatorsStatistics> {
    const url = `${environment.mkgoURL}/api/v1/os/statistics`;
    const options = {
      headers: { 'Company-CNPJ': this._dataService.company.cnpj },
      params: {
        receiveDateStart,
        receiveDateEnd
      }
    };

    return firstValueFrom(this._http.get<IIndicatorsStatistics>(url, options));
  }

  async getRosWithSomePart(partId: string) {
    const url = `${environment.mkgoURL}/api/v1/os/any-os/parts/${partId}`;
    const options = await firstValueFrom(this._dataService.httpOptions(this._isFromMatrix));
    const response = await firstValueFrom(this._http.get<{ os: MkgOS[] }>(url, options).pipe(first()));
    return response.os;
  }

  /**
   * 
   * @param idRo 
   * @param parts 
   * @param instanceService 
   * @param newState 
   * @param validateIf only change state if current state is equal to this value
   */
  async changeStatePartsInRo(idRo: ObjectId, parts: MkgOSPart[], instanceService: RoPartService, newState: PartState, validateIf?: PartState) {
    const requests = [];
    for (const part of parts) {
      if (validateIf !== undefined) {
        if (part.available === validateIf.id) {
          part.available = newState.id;
          requests.push(instanceService.addOrUpdate(idRo, part));
        } 
      } else {
        part.available = newState.id;
        requests.push(instanceService.addOrUpdate(idRo, part));
      }
    }

    return Promise.all(requests);
  }

  /**
   * 
   * @param idRo 
   * @param labors 
   * @param instanceService 
   * @param newState 
   * @param validateIf only change state if current state is equal to this value
   */
  async changeStateLaborsInRo(idRo: ObjectId, labors: OsLabor[], instanceService: RoLaborService, newState: LaborState, validateIf?: LaborState) {
    const requests = [];
    for (const labor of labors) {
      if (validateIf !== undefined) {
        if (labor.available === validateIf.id) {
          labor.available = newState.id;
          requests.push(instanceService.addOrUpdate(idRo, labor));
        }
      } else {
        labor.available = newState.id;
        requests.push(instanceService.addOrUpdate(idRo, labor));
      }
    }

    return Promise.all(requests);
  }
}
